import { useMemo, useState } from 'react';
import { Button, Field, Icon, Modal } from './ui';
import { ApiError, api } from '../lib/api';
import type { TreeNode } from '../lib/types';

function flatten(node: TreeNode, depth = 0): Array<{ node: TreeNode; depth: number }> {
  return [
    { node, depth },
    ...(node.children ?? []).flatMap((child) => flatten(child, depth + 1)),
  ];
}

/** Everything at or beneath a node, so it can be excluded as a destination. */
function subtreeIds(node: TreeNode): Set<string> {
  const ids = new Set<string>([node.id]);
  for (const child of node.children ?? []) {
    for (const id of subtreeIds(child)) ids.add(id);
  }
  return ids;
}

/** How deep a subtree runs, counting the node itself as one. */
function depthOf(node: TreeNode): number {
  return 1 + Math.max(0, ...(node.children ?? []).map(depthOf));
}

const LEVELS = ['L0', 'L1', 'L2', 'L3', 'L4'];

/**
 * Moving an office, with everything under it.
 *
 * The destination list is filtered rather than validated after the fact: an
 * office cannot go inside itself, and a subtree cannot land somewhere too
 * shallow to hold its own depth. The server refuses both, but a dropdown that
 * only offers legal answers is kinder than an error after the fact.
 */
export function MoveOffice({
  node,
  tree,
  onClose,
  onMoved,
}: {
  node: TreeNode | null;
  tree: TreeNode | null;
  onClose: () => void;
  onMoved?: () => void;
}) {
  const [parentId, setParentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(() => {
    if (!node || !tree) return [];
    const excluded = subtreeIds(node);
    const needs = depthOf(node);
    return flatten(tree).filter(({ node: candidate }) => {
      if (excluded.has(candidate.id)) return false;
      if (candidate.id === node.parentId) return false;
      // The deepest member would land at this many levels below the candidate.
      return LEVELS.indexOf(candidate.level) + needs < LEVELS.length;
    });
  }, [node, tree]);

  async function submit() {
    if (!node || !parentId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/org/nodes/${node.id}/move`, { newParentId: parentId });
      setParentId('');
      onMoved?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const destination = options.find((o) => o.node.id === parentId)?.node;

  return (
    <Modal
      open={node !== null}
      title={`Move ${node?.name ?? ''}`}
      description="Everything underneath moves with it — people, merchants, applications and history."
      onClose={() => {
        setParentId('');
        setError(null);
        onClose();
      }}
    >
      <div className="stack">
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}

        {options.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            There is nowhere to put it. An office cannot move inside itself, and
            nothing beneath it can end up below an agent.
          </p>
        ) : (
          <>
            <Field
              label="Move it under"
              hint="Only offices with room for it are listed."
              htmlFor="move-parent"
            >
              <select
                id="move-parent"
                className="input select"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <option value="">Choose an office…</option>
                {options.map(({ node: candidate, depth }) => (
                  <option key={candidate.id} value={candidate.id}>
                    {'  '.repeat(depth)}
                    {candidate.name} · {candidate.level}
                  </option>
                ))}
              </select>
            </Field>

            {destination && node && (
              <div className="notice">
                {Icon.alert({ size: 15 })}
                <span>
                  {node.name} becomes{' '}
                  <strong>{LEVELS[LEVELS.indexOf(destination.level) + 1]}</strong>{' '}
                  {node.level !== LEVELS[LEVELS.indexOf(destination.level) + 1] && (
                    <>(it is {node.level} today){' '}</>
                  )}
                  and takes {destination.name}'s pricing floor if that floor is
                  tighter than its own.
                </span>
              </div>
            )}
          </>
        )}

        <div className="row">
          <Button
            variant="primary"
            disabled={busy || !parentId}
            onClick={() => void submit()}
          >
            {busy ? 'Moving…' : 'Move it'}
          </Button>
          <Button
            onClick={() => {
              setParentId('');
              setError(null);
              onClose();
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
